// Home page: full-bleed hero → stat band → services grid → network teaser →
// partner strip → CTA band. Bilingual via the `home` i18n namespace, on-brand,
// animated with the shared motion variants. Owned by Agent D.
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DEFAULT_LOCALE } from "../i18n";
import { fadeUp, stagger, revealViewport } from "../motion/variants";
import Hero from "../components/Hero";
import StatBand from "../components/StatBand";
import SectionBlock from "../components/SectionBlock";
import ServiceCard, { type ServiceIcon } from "../components/ServiceCard";
import RouteMap from "../components/RouteMap";
import PartnerStrip, { CARRIERS } from "../components/PartnerStrip";
import PageMeta from "../components/PageMeta";

const SERVICE_KEYS: { key: string; icon: ServiceIcon }[] = [
  { key: "container", icon: "container" },
  { key: "vessel", icon: "vessel" },
  { key: "forwarding", icon: "forwarding" },
  { key: "trade", icon: "trade" },
];

export default function Home() {
  const { t } = useTranslation("home");
  const { lang } = useParams();
  const current = lang ?? DEFAULT_LOCALE;
  const isZh = current === "zh"; // zh-first: Chinese heading leads, Latin caption follows

  return (
    <div className="page page-home">
      <PageMeta title={t("meta.title")} description={t("meta.description")} lang={current} />

      <Hero lang={current} />
      <StatBand />

      {/* Services grid */}
      <SectionBlock
        eyebrowEn={t("services.eyebrowEn")}
        heading={t("services.heading")}
        lead={t("services.lead")}
        centered
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICE_KEYS.map(({ key, icon }) => (
            <ServiceCard
              key={key}
              icon={icon}
              title={t(`services.items.${key}.title`)}
              desc={t(`services.items.${key}.desc`)}
            />
          ))}
        </div>
        <motion.div
          variants={fadeUp}
          className="mt-10 text-center"
        >
          <Link
            to={`/${current}/services`}
            className="inline-flex items-center gap-2 font-medium text-[var(--brand-azure-text)] underline-offset-4 hover:underline"
          >
            {t("services.viewAll")}
            <span aria-hidden="true">→</span>
          </Link>
        </motion.div>
      </SectionBlock>

      {/* Network teaser */}
      <section className="bg-[var(--brand-tint-3)] py-[var(--space-section)]">
        <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-6 lg:grid-cols-2">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
          >
            {isZh ? (
              <>
                <h2 className="font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]">
                  {t("network.heading")}
                </h2>
                <p className="mt-3 font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]">
                  {t("network.eyebrowEn")}
                </p>
              </>
            ) : (
              <>
                <p className="font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]">
                  {t("network.eyebrowEn")}
                </p>
                <h2 className="mt-3 font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]">
                  {t("network.heading")}
                </h2>
              </>
            )}
            <p className="mt-5 max-w-lg text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]">
              {t("network.lead")}
            </p>
            <Link
              to={`/${current}/network`}
              className="mt-8 inline-flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-6 py-3 font-medium text-white transition-colors hover:bg-[var(--brand-azure)]"
            >
              {t("network.cta")}
            </Link>
          </motion.div>
          <RouteMap
            hubLabel={t("network.domesticLabel")}
            ariaLabel={t("map.ariaLabel", { ns: "network" })}
          />
        </div>
      </section>

      {/* Partner strip */}
      <SectionBlock
        eyebrowEn={t("partners.eyebrowEn")}
        heading={t("partners.heading")}
        lead={t("partners.lead")}
        centered
      >
        <PartnerStrip partners={CARRIERS.slice(0, 8)} lang={current} columns={4} />
        <motion.div variants={fadeUp} className="mt-10 text-center">
          <Link
            to={`/${current}/partners`}
            className="inline-flex items-center gap-2 font-medium text-[var(--brand-azure-text)] underline-offset-4 hover:underline"
          >
            {t("partners.cta")}
            <span aria-hidden="true">→</span>
          </Link>
        </motion.div>
      </SectionBlock>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-azure)]">
        <div className="mx-auto w-full max-w-4xl px-6 py-[var(--space-section)] text-center">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={revealViewport}
          >
            {isZh ? (
              <>
                <motion.h2
                  variants={fadeUp}
                  className="font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-white"
                >
                  {t("cta.title")}
                </motion.h2>
                <motion.p
                  variants={fadeUp}
                  className="mt-3 font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-white/80"
                >
                  {t("cta.eyebrowEn")}
                </motion.p>
              </>
            ) : (
              <>
                <motion.p
                  variants={fadeUp}
                  className="font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-white/80"
                >
                  {t("cta.eyebrowEn")}
                </motion.p>
                <motion.h2
                  variants={fadeUp}
                  className="mt-4 font-zh text-[var(--text-3xl)] font-bold leading-[var(--leading-heading)] text-white"
                >
                  {t("cta.title")}
                </motion.h2>
              </>
            )}
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-5 max-w-2xl text-[var(--text-lg)] leading-[var(--leading-body)] text-white/90"
            >
              {t("cta.subtitle")}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10">
              <Link
                to={`/${current}/contact`}
                className="inline-flex items-center rounded-[var(--radius-md)] bg-white px-8 py-4 font-medium text-[var(--brand-azure-text)] shadow-[var(--shadow-card)] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("cta.button")}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
